import 'react-app-polyfill/ie9';
import 'react-app-polyfill/stable';
import 'slick-carousel/slick/slick.css';
import './assets/css/global.scss';
import './assets/css/index.scss';

import {ModelManager} from '@adobe/aem-spa-page-model-manager';
import {setupListeners} from '@reduxjs/toolkit/query';
import React from 'react';
import ReactDOM from 'react-dom/client';
import {Provider} from 'react-redux';

import appConfig from '@/config/appConfig.typed';
import {initConfig} from '@/config/config';
import App from '@/containers/App/App';
import ErrorHandlingPage from '@/containers/ErrorHandlingPage/ErrorHandlingPage';
import aemService from '@/services/aem/aemService';
import ageGateService from '@/services/ageGate/ageGateService';
import {initInstallPrompt} from '@/services/installPromptService';
import languageDataService from '@/services/localization/languageDataService';
import log from '@/services/logger/log';
import marketService from '@/services/marketService';
import pushNotificationService from '@/services/pushNotification/pushNotificationService';
import * as pathService from '@/services/route/pathService';
import {setIsAem} from '@/state/slices/globalSlice';
import {addReduxLoggerIfEnabled, configureStore, dispatch} from '@/state/store';
import helpers from '@/utils/helpers';

import {setupManifest} from './manifest';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';

const start = async () => {
    const timeOut = parseInt(localStorage.getItem('startTimeout') || 0);

    if (timeOut) await helpers.timeout(timeOut);

    const store = configureStore();

    try {
        const isMarketCodeValid = marketService.validateAndInitiateMarketCode();

        if (!isMarketCodeValid) {
            throw new Error(`MarketCode is not valid, location: ${window.location}`);
        }

        setupListeners(store.dispatch);

        await initConfig(marketService.getMarketCode());

        log.setLevel(appConfig.getLogLevel(), appConfig.getAwsLogLevel());
        setupManifest();

        pushNotificationService.initializeFirebaseInServiceWorker();
        await addReduxLoggerIfEnabled();

        aemService.initAemComponents();

        await ageGateService.initAgeGate();
        const isInitialized = await languageDataService.initLanguage();

        if (!isInitialized) return;

        // Initialize the ModelManager before invoking root.render(..).
        await initAem();

        render(store, App);
    } catch (e) {
        log.error(e);
        render(store, ErrorHandlingPage);
    }
};

const initAem = async () => {
    const isAem = pathService.isAemPath();

    dispatch(setIsAem(isAem));

    await ModelManager.initialize(`${pathService.getFullAemPath()}/home`);
};

const render = (store, Component) => {
    const isAem = pathService.isAemPath();
    const root = ReactDOM.createRoot(document.getElementById('root'));

    if (isAem) {
        document.body.classList.add('aem-container');
    }

    root.render(
        <Provider store={store}>
            <Component />
        </Provider>
    );
};

start();

serviceWorkerRegistration.register();

window.addEventListener('beforeinstallprompt', initInstallPrompt);
